import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import api from '../../api/client';
import { listJobs } from '../../api/jobsApi';
import { Button } from '../../components/ui/button';
import { formatCurrency } from '../../utils/formatters';
import './QuoteDetail.css';
import BookingDocumentsPanel from '../../components/Operations/Documents/BookingDocumentsPanel';

const IMAGE_EXTS = /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)(\?|$)/i;
const isImageUrl = (url) => IMAGE_EXTS.test(String(url || ''));
const text = (value) => String(value || '').trim();

// Surface the API's error verbatim — a string detail/error as-is, and a DRF
// field-error object spelled out ("customer_id: This field is required.")
// rather than collapsed into a generic "couldn't save".
const apiErrorText = (err, fallback = 'Request failed.') => {
  const d = err?.response?.data;
  if (typeof d === 'string' && d.trim()) return d.trim();
  if (d && typeof d === 'object') {
    if (typeof d.detail === 'string' && d.detail) return d.detail;
    if (typeof d.error === 'string' && d.error) return d.error;
    const parts = Object.entries(d)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(' ') : String(v)}`)
      .filter(Boolean);
    if (parts.length) return parts.join('  •  ');
  }
  return err?.message || fallback;
};

const API_BASE = (process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'https://os.abon.ai/api').replace(/\/api\/?$/, '');

const parseItems = (quote) => {
  if (Array.isArray(quote?.items)) return quote.items;
  if (Array.isArray(quote?.line_items)) return quote.line_items;
  return [];
};

const parseOptions = (quote) => {
  if (Array.isArray(quote?.options)) return quote.options;
  return [];
};

const parseAttachments = (quote) =>
  [
    quote?.scanned_file,
    quote?.attachment,
    quote?.pdf_url,
    quote?.file_url,
    ...(Array.isArray(quote?.attachments) ? quote.attachments : []),
  ]
    .flat()
    .filter(Boolean)
    .map((item) => (typeof item === 'string' ? item : item?.url || item?.file || item?.name || ''))
    .filter(Boolean);

const fetchQuoteDetail = async (quoteId) => {
  try {
    return await api.get(`/quotes/${quoteId}/`);
  } catch (error) {
    if (Number(error?.response?.status || 0) !== 404) throw error;
    return api.get(`/revival/${quoteId}/`);
  }
};

const patchQuoteDetail = async (quoteId, payload) => {
  try {
    return await api.patch(`/quotes/${quoteId}/`, payload);
  } catch (error) {
    if (Number(error?.response?.status || 0) !== 404) throw error;
    return api.patch(`/revival/${quoteId}/`, payload);
  }
};

const createQuoteCopy = async (payload) => {
  try {
    return await api.post('/quotes/', payload);
  } catch (error) {
    if (Number(error?.response?.status || 0) !== 404) throw error;
    return api.post('/revival/', payload);
  }
};

const convertQuote = async (quoteId) => {
  try {
    return await api.post(`/quotes/${quoteId}/convert/`);
  } catch (error) {
    if (Number(error?.response?.status || 0) !== 404) throw error;
    return api.post(`/revival/${quoteId}/convert/`);
  }
};

const sendQuote = async (quoteId, { delivery = 'sms', phone = '', email = '' } = {}) => {
  const payload = { delivery };
  if (delivery === 'sms' && phone) payload.phone = phone;
  if (delivery === 'email' && email) payload.email = email;
  try {
    return await api.post(`/quotes/${quoteId}/send/`, payload);
  } catch (error) {
    if (Number(error?.response?.status || 0) !== 404) throw error;
    return api.post(`/revival/${quoteId}/send-again/`, payload);
  }
};

const buildDuplicatePayload = (quote) => ({
  title: text(quote?.title) ? `${text(quote.title)} Copy` : 'Quote Copy',
  description: text(quote?.description),
  notes: text(quote?.notes),
  status: 'draft',
  service_type: text(quote?.service_type),
  customer: quote?.customer?.id || quote?.customer_id || undefined,
  customer_id: quote?.customer?.id || quote?.customer_id || undefined,
  customer_name: text(quote?.customer_name),
  customer_phone: text(quote?.customer_phone),
  customer_email: text(quote?.customer_email),
  lead: quote?.lead?.id || quote?.lead_id || undefined,
  lead_id: quote?.lead?.id || quote?.lead_id || undefined,
  address: text(quote?.address),
  city: text(quote?.city),
  state: text(quote?.state),
  zip_code: text(quote?.zip_code || quote?.zip),
  estimated_total: Number(quote?.estimated_total ?? quote?.total ?? 0) || 0,
  total: Number(quote?.total ?? quote?.estimated_total ?? 0) || 0,
  line_items: parseItems(quote).map((item) => ({
    description: text(item?.description),
    quantity: Number(item?.quantity || 1) || 1,
    unit_price: Number(item?.unit_price ?? item?.price ?? item?.amount ?? 0) || 0,
    total_price: Number(item?.total_price ?? item?.amount ?? item?.unit_price ?? item?.price ?? 0) || 0,
  })),
});

export default function QuoteDetail({ quoteId: quoteIdProp, onClose, embedded = false, inWindow = false } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  // Reachable two ways: the /quotes/:quoteId route (useParams) and, embedded in
  // a drawer from the customer card, a quoteId prop. The prop wins. "Back"/close
  // calls onClose in the drawer and history-back on the page. In the movable
  // quote window (Revival) the window's own × closes it, so there is no Back.
  const { quoteId: quoteIdParam = '' } = useParams();
  const quoteId = quoteIdProp || quoteIdParam;
  const goBack = onClose || (() => navigate(-1));

  const [quote, setQuote] = useState(null);
  const [linkedJob, setLinkedJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    service_type: '',
    status: 'draft',
    notes: '',
  });
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendDelivery, setSendDelivery] = useState('sms');
  const [sendPhone, setSendPhone] = useState('');
  const [sendEmail, setSendEmail] = useState('');
  // /send/ returns 409 {requires_pricing, warning} on a $0 total (or a $0
  // option); the warning shows inline next to Send — only an entered price
  // clears it. (Backend: feat/quote-price-override.)
  const [sendWarning, setSendWarning] = useState('');

  // Editable line items (the builder). Each row: description, quantity,
  // unit_price (string, EMPTY by default — never prefilled from a service),
  // total computed live. Seeded from the quote on load; saved via PATCH.
  const [lineItems, setLineItems] = useState([]);
  const [savingLines, setSavingLines] = useState(false);
  const isLocked = Boolean(quote?.sent_at);

  // Tiered options (events: "Full (5 hr)" / "Essentials (3 hr)"). Each option
  // carries its OWN entered-price line items; the customer picks one on the
  // public page. A quote is EITHER flat line items OR options — optionRows seeds
  // from the quote; "Use tiered options" switches a flat draft over.
  const [optionRows, setOptionRows] = useState([]);
  const [savingOptions, setSavingOptions] = useState(false);

  const [quoteFiles, setQuoteFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const loadQuoteFiles = async () => {
    if (!quoteId) return;
    try {
      const resp = await api.get('/storage/files/', { params: { quote: quoteId } });
      const data = resp?.data;
      const list = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
      setQuoteFiles(list);
    } catch { /* gallery stays empty */ }
  };

  const handlePhotoUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('category', 'job_image');
        fd.append('quote', quoteId);
        // eslint-disable-next-line no-await-in-loop
        await api.post('/storage/files/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(`${files.length} photo${files.length === 1 ? '' : 's'} uploaded`);
      await loadQuoteFiles();
    } catch (err) {
      toast.error(text(err?.response?.data?.detail || err?.response?.data?.error || err?.message || 'Upload failed'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetchQuoteDetail(quoteId);
        if (!cancelled) {
          const next = response?.data || null;
          setQuote(next);
          setForm({
            // "Untitled Quote" is the server's placeholder, not a real title —
            // show it as empty so the field invites a real one.
            title: text(next?.title) === 'Untitled Quote' ? '' : text(next?.title || next?.name),
            description: text(next?.description),
            service_type: text(next?.service_type),
            status: text(next?.status || 'draft') || 'draft',
            notes: text(next?.notes),
          });
          setLineItems(
            parseItems(next).map((it) => ({
              description: text(it?.description || it?.name),
              quantity: String(it?.quantity ?? it?.qty ?? 1),
              unit_price: it?.unit_price != null ? String(it.unit_price) : (it?.price != null ? String(it.price) : ''),
            }))
          );
          setOptionRows(
            parseOptions(next).map((opt) => ({
              name: text(opt?.name),
              description: text(opt?.description),
              is_recommended: Boolean(opt?.is_recommended),
              items: (Array.isArray(opt?.items) ? opt.items : []).map((it) => ({
                description: text(it?.description || it?.name),
                quantity: String(it?.quantity ?? 1),
                unit_price: it?.unit_price != null ? String(it.unit_price) : '',
              })),
            }))
          );
          void loadQuoteFiles();
          // A converted quote has a Job — surface the link to it.
          try {
            const jobsRes = await listJobs({ quote: quoteId });
            const jobs = Array.isArray(jobsRes?.data?.results)
              ? jobsRes.data.results
              : Array.isArray(jobsRes?.data)
              ? jobsRes.data
              : [];
            if (!cancelled) setLinkedJob(jobs[0] || null);
          } catch {
            if (!cancelled) setLinkedJob(null);
          }

          // Auto-open send modal when navigated with autoSend state
          if (location?.state?.autoSend && next) {
            setSendPhone(text(next?.customer_phone));
            setSendEmail(text(next?.customer_email));
            setSendDelivery(text(next?.customer_email) ? 'email' : 'sms');
            setShowSendModal(true);
            // Clear the state so refresh doesn't re-trigger
            window.history.replaceState({}, document.title);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            text(err?.response?.data?.detail || err?.response?.data?.error || err?.message || 'Unable to load quote detail.')
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (quoteId) void load();
    else {
      setLoading(false);
      setError('Quote ID is missing.');
    }

    return () => { cancelled = true; };
  }, [quoteId]);

  const items = useMemo(() => parseItems(quote), [quote]);
  const options = useMemo(() => parseOptions(quote), [quote]);
  const hasOptions = options.length > 0;
  const attachments = useMemo(() => parseAttachments(quote), [quote]);
  const total = Number(quote?.estimated_total ?? quote?.total ?? 0) || 0;
  const publicToken = text(quote?.public_token);
  const customerId = text(
    quote?.customer?.id || quote?.customer_id || quote?.customer?.customer_id || quote?.customer_external_id
  );

  // SMS quote-sending is gated: today the frontend can only see whether the
  // customer has a phone on file. A tenant-level quote_send capability isn't
  // exposed to the client yet — when it is, AND it here. Until then, no phone =
  // email only, with a reason, and an SMS attempt that the tenant has disabled
  // surfaces its API error readably (apiErrorText) instead of failing silently.
  const smsEnabled = Boolean(text(quote?.customer_phone));
  const smsDisabledReason = 'Texting this quote isn’t available — no phone number on file for this customer. Send it by email.';

  const openSendModal = () => {
    const phone = text(quote?.customer_phone);
    const email = text(quote?.customer_email);
    setSendPhone(phone);
    setSendEmail(email);
    setSendDelivery(smsEnabled && !email ? 'sms' : 'email');
    setSendWarning('');
    setShowSendModal(true);
  };

  const handleSend = async () => {
    const recipient = sendDelivery === 'sms' ? sendPhone : sendEmail;
    if (!recipient.trim()) {
      toast.error(`Enter a ${sendDelivery === 'sms' ? 'phone number' : 'email address'}`);
      return;
    }
    setSaving(true);
    setError('');
    setSendWarning('');
    try {
      await sendQuote(quoteId, { delivery: sendDelivery, phone: sendPhone, email: sendEmail });
      toast.success(`Quote sent via ${sendDelivery === 'sms' ? 'SMS' : 'email'} to ${recipient}`);
      setShowSendModal(false);
      const response = await fetchQuoteDetail(quoteId);
      setQuote(response?.data || null);
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 409 && data?.requires_pricing) {
        // Not an error — the quote needs a price. Keep the modal open and show
        // the backend's warning (it names any $0 options) right next to Send.
        setSendWarning(text(data.warning) || 'This quote totals $0 — add a price before sending.');
      } else {
        setShowSendModal(false);
        setError(apiErrorText(err, 'Failed to send quote.'));
      }
    } finally {
      setSaving(false);
    }
  };

  const openPreview = () => {
    if (publicToken) {
      window.open(`${API_BASE}/revival/quote/${publicToken}/`, '_blank');
    } else {
      toast.error('No public link available — save the quote first.');
    }
  };

  const runAction = async (type) => {
    setSaving(true);
    setError('');
    try {
      if (type === 'duplicate') {
        const res = await createQuoteCopy(buildDuplicatePayload(quote));
        const nextId = text(res?.data?.id || res?.data?.quote_id);
        if (nextId) { navigate(`/quotes/${nextId}`); return; }
      }
      if (type === 'convert') await convertQuote(quoteId);
      const response = await fetchQuoteDetail(quoteId);
      setQuote(response?.data || null);
      if (type === 'convert') {
        try {
          const jobsRes = await listJobs({ quote: quoteId });
          const jobs = Array.isArray(jobsRes?.data?.results)
            ? jobsRes.data.results
            : Array.isArray(jobsRes?.data)
            ? jobsRes.data
            : [];
          setLinkedJob(jobs[0] || null);
        } catch { /* link renders on next load */ }
      }
    } catch (err) {
      setError(apiErrorText(err, 'Quote action failed.'));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        title: text(form.title),
        description: text(form.description),
        service_type: text(form.service_type),
        status: text(form.status || 'draft') || 'draft',
        notes: text(form.notes),
      };
      const response = await patchQuoteDetail(quoteId, payload);
      setQuote((prev) => ({ ...(prev || {}), ...(response?.data || {}), ...payload }));
      setEditing(false);
    } catch (err) {
      setError(apiErrorText(err, 'Unable to save quote.'));
    } finally {
      setSaving(false);
    }
  };

  // ── Line-item editor ──
  const numOr0 = (v) => {
    const n = Number(String(v ?? '').replace(/[$,]/g, '').trim());
    return Number.isFinite(n) ? n : 0;
  };
  const liveTotal = useMemo(
    () => lineItems.reduce((sum, li) => sum + numOr0(li.quantity) * numOr0(li.unit_price), 0),
    [lineItems]
  );
  const addLine = () => setLineItems((rows) => [...rows, { description: '', quantity: '1', unit_price: '' }]);
  const updateLine = (i, key, value) =>
    setLineItems((rows) => rows.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));
  const removeLine = (i) => setLineItems((rows) => rows.filter((_, idx) => idx !== i));

  // Build the API payload for the flat line items. Empty price stays null
  // ("not priced yet"), never coerced to $0 — a real 0 is a deliberate
  // complimentary line (the backend renders a null line as "Included").
  const buildLines = (rows) =>
    rows
      .filter((li) => text(li.description) || li.unit_price !== '' || numOr0(li.quantity) !== 1)
      .map((li) => ({
        description: text(li.description),
        quantity: numOr0(li.quantity) || 1,
        unit_price: li.unit_price === '' ? null : numOr0(li.unit_price),
        total_price: li.unit_price === '' ? null : (numOr0(li.quantity) || 1) * numOr0(li.unit_price),
      }));

  const seedLinesFrom = (next) =>
    parseItems(next).map((it) => ({
      description: text(it?.description || it?.name),
      quantity: String(it?.quantity ?? 1),
      unit_price: it?.unit_price != null ? String(it.unit_price) : '',
    }));

  // Signature of what the server currently holds, so a blur that changed
  // nothing is a no-op (numOr0 normalizes "500" vs "500.00" to the same value).
  const lastLinesSig = useRef(null);
  useEffect(() => {
    if (quote) lastLinesSig.current = JSON.stringify(buildLines(lineItems));
    // Re-baseline only when a different quote loads, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.id]);

  // reseed:true  → "Save line items" button: pull the server's version back
  //                into the editor (picks up null→Included normalization).
  // reseed:false → blur / Enter autosave: persist, but KEEP the user's live
  //                client rows so a save response never clobbers the field they
  //                just tabbed into.
  const persistLines = async ({ reseed }) => {
    if (isLocked || savingLines) return;
    const items = buildLines(lineItems);
    const sig = JSON.stringify(items);
    if (lastLinesSig.current !== null && sig === lastLinesSig.current) return; // unchanged
    setSavingLines(true);
    setError('');
    try {
      const response = await patchQuoteDetail(quoteId, { items });
      const next = response?.data;
      if (next) {
        setQuote(next);
        lastLinesSig.current = JSON.stringify(buildLines(seedLinesFrom(next)));
        if (reseed) setLineItems(seedLinesFrom(next));
      } else {
        lastLinesSig.current = sig;
      }
    } catch (err) {
      setError(apiErrorText(err, 'Unable to save line items.'));
    } finally {
      setSavingLines(false);
    }
  };
  const saveLineItems = () => persistLines({ reseed: true });
  const commitLines = () => persistLines({ reseed: false });
  // Enter commits the field (blur → autosave); Shift+Enter is left alone.
  const onLineKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  // Title autosaves on blur/Enter too, so the inline title field behaves like
  // the line editor — type a name, click away, it's saved.
  const lastTitleSig = useRef(null);
  useEffect(() => {
    if (quote) lastTitleSig.current = text(form.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.id]);
  const commitTitle = async () => {
    if (isLocked) return;
    const title = text(form.title);
    if (title === (lastTitleSig.current ?? '')) return;
    try {
      const response = await patchQuoteDetail(quoteId, { title });
      setQuote((prev) => ({ ...(prev || {}), ...(response?.data || {}), title }));
      lastTitleSig.current = title;
    } catch (err) {
      setError(apiErrorText(err, 'Unable to save the title.'));
    }
  };
  const onTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  // ── Tiered options editor ──
  const emptyOptionItem = () => ({ description: '', quantity: '1', unit_price: '' });
  const optionTotal = (opt) =>
    (opt.items || []).reduce((s, it) => s + numOr0(it.quantity) * numOr0(it.unit_price), 0);
  const addOption = () =>
    setOptionRows((rows) => [
      ...rows,
      { name: `Option ${rows.length + 1}`, description: '', is_recommended: rows.length === 0, items: [emptyOptionItem()] },
    ]);
  const updateOption = (oi, key, value) =>
    setOptionRows((rows) => rows.map((o, i) => (i === oi ? { ...o, [key]: value } : o)));
  const removeOption = (oi) => setOptionRows((rows) => rows.filter((_, i) => i !== oi));
  const setRecommended = (oi) =>
    setOptionRows((rows) => rows.map((o, i) => ({ ...o, is_recommended: i === oi })));
  const addOptionItem = (oi) =>
    setOptionRows((rows) => rows.map((o, i) => (i === oi ? { ...o, items: [...o.items, emptyOptionItem()] } : o)));
  const updateOptionItem = (oi, ii, key, value) =>
    setOptionRows((rows) =>
      rows.map((o, i) => (i === oi ? { ...o, items: o.items.map((it, j) => (j === ii ? { ...it, [key]: value } : it)) } : o))
    );
  const removeOptionItem = (oi, ii) =>
    setOptionRows((rows) => rows.map((o, i) => (i === oi ? { ...o, items: o.items.filter((_, j) => j !== ii) } : o)));

  // Switch a flat draft to tiered options: carry the flat lines into the first
  // option, add an empty second (the two-option shape events quotes want).
  const useTieredOptions = () => {
    const seedItems = lineItems.length
      ? lineItems.map((li) => ({ ...li }))
      : [emptyOptionItem()];
    setOptionRows([
      { name: 'Full', description: '', is_recommended: true, items: seedItems },
      { name: 'Essentials', description: '', is_recommended: false, items: [emptyOptionItem()] },
    ]);
  };

  const buildOptionsPayload = () =>
    optionRows.map((o) => ({
      name: text(o.name) || 'Option',
      description: text(o.description),
      is_recommended: Boolean(o.is_recommended),
      items: (o.items || [])
        .filter((it) => text(it.description) || it.unit_price !== '' || numOr0(it.quantity) !== 1)
        .map((it) => ({
          description: text(it.description),
          quantity: numOr0(it.quantity) || 1,
          unit_price: it.unit_price === '' ? null : numOr0(it.unit_price),
          total_price: it.unit_price === '' ? null : (numOr0(it.quantity) || 1) * numOr0(it.unit_price),
        })),
    }));

  const saveOptions = async () => {
    setSavingOptions(true);
    setError('');
    try {
      // Full replace (per the serializer). Sending options clears the flat items.
      const response = await patchQuoteDetail(quoteId, { options: buildOptionsPayload(), items: [] });
      const next = response?.data;
      if (next) setQuote(next);
    } catch (err) {
      setError(apiErrorText(err, 'Unable to save options.'));
    } finally {
      setSavingOptions(false);
    }
  };

  // The bottom-bar Save: persist the title + the line items in one PATCH so the
  // operator never has to open a separate Edit panel to keep their work.
  const handleSaveAll = async () => {
    setSaving(true);
    setError('');
    try {
      const items = lineItems
        .filter((li) => text(li.description) || li.unit_price !== '' || numOr0(li.quantity) !== 1)
        .map((li) => ({
          description: text(li.description),
          quantity: numOr0(li.quantity) || 1,
          // Empty price stays null ("not priced yet"), never coerced to $0 —
          // a real 0 is a deliberate complimentary line. (Per the price-override
          // contract; the backend renders a null line as "Included".)
          unit_price: li.unit_price === '' ? null : numOr0(li.unit_price),
          total_price: li.unit_price === '' ? null : (numOr0(li.quantity) || 1) * numOr0(li.unit_price),
        }));
      const payload = {
        title: text(form.title),
        description: text(form.description),
        notes: text(form.notes),
        status: text(form.status || 'draft') || 'draft',
      };
      if (!isLocked) {
        // A quote is EITHER tiered options OR flat line items — save whichever
        // the operator is building.
        if (optionRows.length > 0) {
          payload.options = buildOptionsPayload();
          payload.items = [];
        } else {
          payload.items = items;
        }
      }
      const response = await patchQuoteDetail(quoteId, payload);
      const next = response?.data;
      if (next) {
        setQuote(next);
        setForm((p) => ({ ...p, title: text(next?.title) === 'Untitled Quote' ? '' : text(next?.title) }));
        setLineItems(
          parseItems(next).map((it) => ({
            description: text(it?.description || it?.name),
            quantity: String(it?.quantity ?? 1),
            unit_price: it?.unit_price != null ? String(it.unit_price) : '',
          }))
        );
        setOptionRows(
          parseOptions(next).map((opt) => ({
            name: text(opt?.name),
            description: text(opt?.description),
            is_recommended: Boolean(opt?.is_recommended),
            items: (Array.isArray(opt?.items) ? opt.items : []).map((it) => ({
              description: text(it?.description || it?.name),
              quantity: String(it?.quantity ?? 1),
              unit_price: it?.unit_price != null ? String(it.unit_price) : '',
            })),
          }))
        );
      }
    } catch (err) {
      setError(apiErrorText(err, 'Unable to save quote.'));
    } finally {
      setSaving(false);
    }
  };

  // The number shown in the bottom bar / hero while editing: for a tiered quote
  // it's the recommended option's total (what the customer is steered to); for a
  // flat quote it's the live line total.
  const liveDisplayTotal =
    optionRows.length > 0
      ? optionTotal(optionRows.find((o) => o.is_recommended) || optionRows[0] || { items: [] })
      : liveTotal;

  if (loading) {
    return (
      <div className={`quote-detail-page${embedded ? " quote-detail-page--embedded" : ""}`}>
        <div className="quote-detail-shell">
          <p className="quote-detail-copy">Loading quote...</p>
        </div>
      </div>
    );
  }

  if (error && !quote) {
    return (
      <div className={`quote-detail-page${embedded ? " quote-detail-page--embedded" : ""}`}>
        <div className="quote-detail-shell">
          <div className="quote-detail-toolbar">
            <Button variant="ghost" onClick={goBack}>Back</Button>
          </div>
          <div className="quote-detail-card">
            <p className="quote-detail-error">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`quote-detail-page${embedded ? " quote-detail-page--embedded" : ""}${inWindow ? " quote-detail-page--window" : ""}`}>
      <div className="quote-detail-shell">
        {/* Toolbar */}
        <div className="quote-detail-toolbar">
          {inWindow ? <span /> : <Button variant="ghost" onClick={goBack}>Back</Button>}
          <div className="quote-detail-toolbar-actions">
            {customerId ? (
              <Button variant="outline" onClick={() => navigate(`/customers/${customerId}`)}>Customer</Button>
            ) : null}
            <Button variant="outline" onClick={openPreview}>Preview</Button>
            <Button variant="outline" onClick={() => setEditing((c) => !c)}>{editing ? 'Close Edit' : 'Edit'}</Button>
            <Button variant="outline" onClick={() => runAction('duplicate')} disabled={saving}>Duplicate</Button>
            <Button variant="outline" onClick={openSendModal} disabled={saving}>Send</Button>
            {linkedJob ? (
              <Button variant="outline" onClick={() => navigate(`/schedule/jobs/${linkedJob.id}`)}>
                View Job {linkedJob.job_number}
              </Button>
            ) : (
              <Button variant="outline" onClick={() => runAction('convert')} disabled={saving}>Convert to Contract</Button>
            )}
          </div>
        </div>

        {error ? (
          <div className="quote-detail-card"><p className="quote-detail-error">{error}</p></div>
        ) : null}

        {/* Hero */}
        <section className="quote-detail-hero">
          <div>
            <p className="quote-detail-eyebrow">Quote {text(quote?.quote_number)}</p>
            <h1>{text(quote?.customer_name) || 'New quote'}</h1>
            {isLocked ? (
              text(form.title) ? <p className="quote-detail-title-ro">{form.title}</p> : null
            ) : (
              <input
                className="quote-detail-title-input"
                value={form.title}
                placeholder="Quote title (optional) — e.g. Summer Wedding Package"
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                onBlur={commitTitle}
                onKeyDown={onTitleKeyDown}
              />
            )}
            <div className="quote-detail-meta">
              <span className={`quote-status-pill is-${text(quote?.status || 'draft').toLowerCase()}`}>
                {text(quote?.status || 'draft').replace(/_/g, ' ')}
              </span>
              {text(quote?.service_type) ? <span>{quote.service_type}</span> : null}
              {text(quote?.customer_phone) ? <span>{quote.customer_phone}</span> : null}
              {text(quote?.customer_email) ? <span>{quote.customer_email}</span> : null}
            </div>
          </div>
          <div className="quote-total-card">
            <span>Estimated Total</span>
            <strong>{formatCurrency(total)}</strong>
          </div>
        </section>

        {/* Customer + Summary */}
        <div className="quote-detail-grid">
          <section className="quote-detail-card">
            <h2>Customer</h2>
            <dl className="quote-detail-kv">
              <div><dt>Name</dt><dd>{text(quote?.customer_name) || 'N/A'}</dd></div>
              <div><dt>Email</dt><dd>{text(quote?.customer_email) || 'N/A'}</dd></div>
              <div><dt>Phone</dt><dd>{text(quote?.customer_phone) || 'N/A'}</dd></div>
              <div><dt>Address</dt><dd>{text(quote?.address) || 'N/A'}</dd></div>
            </dl>
          </section>
          <section className="quote-detail-card">
            <h2>Quote Summary</h2>
            <dl className="quote-detail-kv">
              <div><dt>Quote Number</dt><dd>{text(quote?.quote_number) || text(quote?.id) || 'N/A'}</dd></div>
              <div><dt>Created</dt><dd>{text(quote?.created_at) ? new Date(quote.created_at).toLocaleString() : 'N/A'}</dd></div>
              <div><dt>Status</dt><dd>{text(quote?.status || 'draft').replace(/_/g, ' ')}</dd></div>
              <div><dt>Source</dt><dd>{text(quote?.source || 'manual')}</dd></div>
              {text(quote?.sent_at) ? <div><dt>Sent</dt><dd>{new Date(quote.sent_at).toLocaleString()}</dd></div> : null}
              {quote?.customer_signed ? <div><dt>Signed</dt><dd>{text(quote?.customer_signature_at) ? new Date(quote.customer_signature_at).toLocaleString() : 'Yes'}</dd></div> : null}
            </dl>
          </section>
        </div>

        {/* Tiered options — editable on an unsent quote. Each option has its own
            priced line items; the customer picks one on the public page. */}
        {!isLocked && optionRows.length > 0 ? (
          <section className="quote-detail-card">
            <div className="quote-detail-section-head">
              <div>
                <h2>Option Packages</h2>
                <p>Each option is priced on its own — the customer picks one. Mark the one you recommend.</p>
              </div>
            </div>
            <div className="quote-options-edit">
              {optionRows.map((opt, oi) => (
                <div key={oi} className={`quote-option-card${opt.is_recommended ? ' is-recommended' : ''}`}>
                  <div className="quote-option-head">
                    <input
                      className="quote-line-input quote-option-name"
                      value={opt.name}
                      placeholder="Option name — e.g. Full (5 hr)"
                      onChange={(e) => updateOption(oi, 'name', e.target.value)}
                    />
                    <label className="quote-option-rec">
                      <input type="radio" name="recommended-option" checked={opt.is_recommended} onChange={() => setRecommended(oi)} />
                      Recommended
                    </label>
                    <span className="quote-option-total">{formatCurrency(optionTotal(opt))}</span>
                    <button type="button" className="quote-line-remove" aria-label="Remove option" onClick={() => removeOption(oi)}>×</button>
                  </div>
                  <input
                    className="quote-line-input quote-option-desc"
                    value={opt.description}
                    placeholder="What this option includes (optional)"
                    onChange={(e) => updateOption(oi, 'description', e.target.value)}
                  />
                  <div className="quote-detail-table-wrap">
                    <table className="quote-detail-table quote-lines-edit">
                      <thead>
                        <tr><th>Description</th><th style={{ width: 70 }}>Qty</th><th style={{ width: 120 }}>Unit price</th><th style={{ width: 100 }}>Total</th><th style={{ width: 40 }} aria-label="Remove" /></tr>
                      </thead>
                      <tbody>
                        {opt.items.map((it, ii) => (
                          <tr key={ii}>
                            <td><input className="quote-line-input" value={it.description} placeholder="Line item" onChange={(e) => updateOptionItem(oi, ii, 'description', e.target.value)} /></td>
                            <td><input className="quote-line-input" type="number" min="0" step="any" value={it.quantity} onChange={(e) => updateOptionItem(oi, ii, 'quantity', e.target.value)} /></td>
                            <td><input className="quote-line-input" type="number" min="0" step="any" value={it.unit_price} placeholder="0.00" onChange={(e) => updateOptionItem(oi, ii, 'unit_price', e.target.value)} /></td>
                            <td className="quote-line-total">{formatCurrency(numOr0(it.quantity) * numOr0(it.unit_price))}</td>
                            <td><button type="button" className="quote-line-remove" aria-label="Remove line" onClick={() => removeOptionItem(oi, ii)}>×</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Button variant="outline" onClick={() => addOptionItem(oi)}>+ Add line</Button>
                </div>
              ))}
            </div>
            <div className="quote-lines-actions">
              <Button variant="outline" onClick={addOption}>+ Add option</Button>
              <Button variant="outline" onClick={saveOptions} disabled={savingOptions}>
                {savingOptions ? 'Saving…' : 'Save options'}
              </Button>
              <Button variant="outline" onClick={() => setOptionRows([])}>Back to a single list</Button>
            </div>
          </section>
        ) : null}

        {/* Option Packages — read-only (sent quote) */}
        {isLocked && hasOptions ? (
          <section className="quote-detail-card">
            <div className="quote-detail-section-head">
              <div>
                <h2>Option Packages</h2>
                <p>{options.length} options available. {quote?.selected_option_id ? 'Customer has selected one.' : 'Customer will choose on the portal.'}</p>
              </div>
            </div>
            <div style={{ display: 'grid', gap: 16 }}>
              {options.map((opt) => {
                const isSelected = quote?.selected_option_id === opt.id;
                const optItems = Array.isArray(opt.items) ? opt.items : [];
                return (
                  <div
                    key={opt.id}
                    style={{
                      border: `1.5px solid ${isSelected ? 'var(--success)' : opt.is_recommended ? 'var(--accent)' : 'var(--border)'}`,
                      borderRadius: 'var(--r-2, 16px)',
                      padding: 18,
                      background: isSelected ? 'var(--success-dim)' : opt.is_recommended ? 'rgba(var(--accent-rgb), 0.04)' : 'transparent',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <strong style={{ fontSize: '1.05rem' }}>{text(opt.name) || 'Unnamed'}</strong>
                        {opt.is_recommended ? (
                          <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: 'rgba(var(--accent-rgb), 0.14)', color: 'var(--accent)', letterSpacing: '0.08em' }}>
                            Recommended
                          </span>
                        ) : null}
                        {isSelected ? (
                          <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: 'var(--success-dim)', color: 'var(--success)', letterSpacing: '0.08em' }}>
                            Selected
                          </span>
                        ) : null}
                      </div>
                      <strong style={{ fontSize: '1.15rem' }}>{formatCurrency(Number(opt.total) || 0)}</strong>
                    </div>
                    {text(opt.description) ? <p style={{ margin: '0 0 10px', color: 'var(--text-sub)', fontSize: '0.9rem' }}>{opt.description}</p> : null}
                    {optItems.length > 0 ? (
                      <div className="quote-detail-table-wrap">
                        <table className="quote-detail-table" style={{ minWidth: 'auto' }}>
                          <thead>
                            <tr>
                              <th>Description</th>
                              <th>Qty</th>
                              <th>Unit Price</th>
                              <th>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {optItems.map((item, idx) => (
                              <tr key={item.id || idx}>
                                <td>{text(item.description) || 'Item'}</td>
                                <td>{Number(item.quantity || 1)}</td>
                                <td>{formatCurrency(Number(item.unit_price || 0))}</td>
                                <td>{formatCurrency(Number(item.total_price ?? 0))}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </section>
        ) : null}

        {/* Flat Line Items (shown when the quote isn't using tiered options).
            Editable on an unsent quote; read-only once sent. */}
        {optionRows.length === 0 ? (
          <section className="quote-detail-card">
            <div className="quote-detail-section-head">
              <div>
                <h2>Line Items</h2>
                <p>{isLocked ? 'Service descriptions, amounts, and totals.' : 'Add each line, set the price, and the total updates as you go.'}</p>
              </div>
              <strong>{formatCurrency(isLocked ? total : liveDisplayTotal)}</strong>
            </div>
            {isLocked ? (
              <div className="quote-detail-table-wrap">
                <table className="quote-detail-table">
                  <thead><tr><th>Description</th><th>Qty</th><th>Unit Price</th><th>Total</th></tr></thead>
                  <tbody>
                    {items.length ? items.map((item, index) => (
                      <tr key={item.id || `${item.description}-${index}`}>
                        <td>{text(item.description) || 'Unnamed item'}</td>
                        <td>{Number(item.quantity || 1) || 1}</td>
                        <td>{formatCurrency(Number(item.unit_price || 0) || 0)}</td>
                        <td>{formatCurrency(Number(item.total_price ?? item.amount ?? 0) || 0)}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan="4" className="quote-detail-empty">No line items.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <>
                <div className="quote-detail-table-wrap">
                  <table className="quote-detail-table quote-lines-edit">
                    <thead>
                      <tr><th>Description</th><th style={{ width: 80 }}>Qty</th><th style={{ width: 130 }}>Unit price</th><th style={{ width: 110 }}>Total</th><th style={{ width: 44 }} aria-label="Remove" /></tr>
                    </thead>
                    <tbody>
                      {lineItems.length === 0 ? (
                        <tr><td colSpan="5" className="quote-detail-empty">No line items yet — add your first line below.</td></tr>
                      ) : lineItems.map((li, i) => (
                        <tr key={i}>
                          <td>
                            <input className="quote-line-input" value={li.description} placeholder="e.g. 5-hour venue rental"
                              onChange={(e) => updateLine(i, 'description', e.target.value)}
                              onBlur={commitLines} onKeyDown={onLineKeyDown} />
                          </td>
                          <td>
                            <input className="quote-line-input" type="number" min="0" step="any" value={li.quantity}
                              onChange={(e) => updateLine(i, 'quantity', e.target.value)}
                              onBlur={commitLines} onKeyDown={onLineKeyDown} />
                          </td>
                          <td>
                            <input className="quote-line-input" type="number" min="0" step="any" value={li.unit_price} placeholder="0.00"
                              onChange={(e) => updateLine(i, 'unit_price', e.target.value)}
                              onBlur={commitLines} onKeyDown={onLineKeyDown} />
                          </td>
                          <td className="quote-line-total">{formatCurrency(numOr0(li.quantity) * numOr0(li.unit_price))}</td>
                          <td>
                            <button type="button" className="quote-line-remove" aria-label="Remove line" onClick={() => removeLine(i)}>×</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="quote-lines-actions">
                  <Button variant="outline" onClick={addLine}>+ Add line</Button>
                  <Button variant="outline" onClick={saveLineItems} disabled={savingLines}>
                    {savingLines ? 'Saving…' : 'Save'}
                  </Button>
                  <Button variant="outline" onClick={useTieredOptions}>Use tiered options</Button>
                  <span className="quote-lines-autosave" aria-live="polite">
                    {savingLines ? 'Saving…' : 'Changes save when you click away or press Enter'}
                  </span>
                </div>
              </>
            )}
          </section>
        ) : null}

        {/* Edit Panel */}
        {editing ? (
          <section className="quote-detail-card">
            <div className="quote-detail-section-head">
              <div><h2>Edit Quote</h2></div>
              <Button variant="outline" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
            <div className="quote-edit-grid">
              <label className="quote-edit-field">
                <span>Title</span>
                <input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
              </label>
              <label className="quote-edit-field">
                <span>Service</span>
                <input value={form.service_type} onChange={(e) => setForm((p) => ({ ...p, service_type: e.target.value }))} />
              </label>
              <label className="quote-edit-field">
                <span>Status</span>
                <input value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} />
              </label>
              <label className="quote-edit-field quote-edit-field-full">
                <span>Description</span>
                <textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} rows={4} />
              </label>
              <label className="quote-edit-field quote-edit-field-full">
                <span>Notes</span>
                <textarea value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} rows={4} />
              </label>
            </div>
          </section>
        ) : null}

        {/* Notes */}
        {text(quote?.notes || quote?.description) ? (
          <section className="quote-detail-card">
            <h2>Notes</h2>
            <p className="quote-detail-notes">{text(quote?.notes || quote?.description)}</p>
          </section>
        ) : null}

        {/* Documents: contracts, waivers, checklists that ride with this booking (Operations → Documents) */}
        {quote?.id ? (
          <section className="quote-detail-card">
            <div className="quote-detail-section-head">
              <div>
                <h2>Documents</h2>
                <p>Contracts, waivers and checklists for this booking, filled in with the client's details.</p>
              </div>
            </div>
            <BookingDocumentsPanel quoteId={quote.id} />
          </section>
        ) : null}

        {/* Photos & Attachments */}
        <section className="quote-detail-card">
          <div className="quote-detail-section-head">
            <div>
              <h2>Photos & Attachments</h2>
              <p>Upload photos to include with the quote.</p>
            </div>
            <div>
              <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handlePhotoUpload} />
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                {uploading ? 'Uploading...' : '+ Upload Photos'}
              </Button>
            </div>
          </div>
          {quoteFiles.length === 0 && attachments.length === 0 ? (
            <p className="quote-detail-copy">No photos or attachments yet.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginTop: 12 }}>
              {quoteFiles.map((f) => {
                const url = f.file || f.url || '';
                const isImg = isImageUrl(url) || (f.mime_type || '').startsWith('image/') || f.category === 'job_image';
                return (
                  <a key={f.id || f.file_uuid || url} href={url} target="_blank" rel="noreferrer"
                    style={{ display: 'block', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-elev)', textDecoration: 'none', color: 'var(--text-main)' }}
                    title={f.name || url}
                  >
                    {isImg ? (
                      <img src={url} alt={f.name || 'Photo'} style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }} />
                    ) : (
                      <div style={{ padding: 16, fontSize: 12, wordBreak: 'break-all' }}>{f.name || url}</div>
                    )}
                    <div style={{ padding: '6px 10px', fontSize: 11, color: 'var(--text-muted)', borderTop: '1px solid var(--border)' }}>{f.category || 'file'}</div>
                  </a>
                );
              })}
              {attachments.map((attachment, index) => {
                const isImg = isImageUrl(attachment);
                return (
                  <a key={`legacy-${attachment}-${index}`} href={attachment} target="_blank" rel="noreferrer"
                    style={{ display: 'block', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-elev)', textDecoration: 'none', color: 'var(--text-main)' }}
                  >
                    {isImg ? (
                      <img src={attachment} alt="Attachment" style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }} />
                    ) : (
                      <div style={{ padding: 16, fontSize: 12, wordBreak: 'break-all' }}>{attachment.split('/').pop()}</div>
                    )}
                  </a>
                );
              })}
            </div>
          )}
        </section>

        {/* Sticky action bar — Save / Preview / Send always reachable at the
            bottom of the builder (shown in the embedded drawer). */}
        {embedded ? (
          <div className="quote-detail-actionbar">
            <div className="quote-detail-actionbar-total">
              <span>Total</span>
              <strong>{formatCurrency(isLocked ? total : liveDisplayTotal)}</strong>
            </div>
            <div className="quote-detail-actionbar-btns">
              <Button variant="outline" onClick={openPreview} disabled={saving}>Preview</Button>
              <Button variant="outline" onClick={handleSaveAll} disabled={saving || savingLines}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="primary" onClick={openSendModal} disabled={saving}>Send</Button>
            </div>
          </div>
        ) : null}
      </div>

      {/* Send Modal with confirmation */}
      {showSendModal ? (
        <div className="quote-send-overlay" onClick={() => setShowSendModal(false)}>
          <div className="quote-send-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Send Quote</h3>
            <p style={{ margin: '0 0 16px', color: 'var(--text-sub)', fontSize: '0.9rem' }}>
              Send to <strong>{text(quote?.customer_name) || 'customer'}</strong>
            </p>
            <div className="quote-send-toggle">
              <button
                type="button"
                className={`quote-send-tab ${sendDelivery === 'sms' ? 'is-active' : ''}`}
                onClick={() => smsEnabled && setSendDelivery('sms')}
                disabled={!smsEnabled}
                title={smsEnabled ? '' : smsDisabledReason}
              >
                SMS
              </button>
              <button type="button" className={`quote-send-tab ${sendDelivery === 'email' ? 'is-active' : ''}`} onClick={() => setSendDelivery('email')}>Email</button>
            </div>
            {!smsEnabled ? (
              <p className="quote-send-note">{smsDisabledReason}</p>
            ) : null}
            {sendDelivery === 'sms' ? (
              <label className="quote-send-field">
                <span>Phone number</span>
                <input type="tel" value={sendPhone} onChange={(e) => setSendPhone(e.target.value)} placeholder="(555) 123-4567" />
              </label>
            ) : (
              <label className="quote-send-field">
                <span>Email address</span>
                <input type="email" value={sendEmail} onChange={(e) => setSendEmail(e.target.value)} placeholder="customer@example.com" />
              </label>
            )}
            <p style={{ margin: '0 0 12px', padding: '10px 14px', borderRadius: 10, background: 'rgba(var(--accent-rgb), 0.08)', color: 'var(--text-sub)', fontSize: '0.82rem', lineHeight: 1.5 }}>
              This will send the quote portal link to{' '}
              <strong style={{ color: 'var(--text-main)' }}>
                {sendDelivery === 'sms' ? sendPhone || '...' : sendEmail || '...'}
              </strong>
            </p>
            {sendWarning ? <p className="quote-send-warning">{sendWarning}</p> : null}
            <div className="quote-send-actions">
              <Button variant="ghost" onClick={() => setShowSendModal(false)}>Cancel</Button>
              <Button
                variant="outline"
                onClick={handleSend}
                disabled={sendDelivery === 'sms' ? !sendPhone.trim() : !sendEmail.trim()}
              >
                Send via {sendDelivery === 'sms' ? 'SMS' : 'Email'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
